import { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { LOGIN_USER } from '@/src/common/gql/mutations/user'
import { router } from 'expo-router'
import { useUserStore } from '@/src/common/store/useUserStore'
import { Platform, ScrollView, View, Text, StyleSheet } from 'react-native'
import { Routes } from '@/src/common/enums/routes'
import { BackButton } from '@/src/common/components/BackButton'
import { Button } from '@/src/common/components/Button'
import { OnboardingWrapper } from '@/src/common/wrappers/OnboardingWrapper'
import { useLogError } from '@/src/common/hooks/useLogError'
import { InputEmail } from '@/src/common/components/Input/InputEmail'
import { joiEmailValidationObject } from '@/src/services/forms'
import { useAccountCreationStore } from '@/src/common/store/useAccountCreationStore'
import { useTrackAccountCreationPage } from '@/src/common/hooks/useTrackAccountCreationPage'
import { AuthenticationE2E } from '@/src/experiences/authentication/e2e'
import { useSardine } from '@/src/services/Sardine'
import { withRecaptchaVerification } from '@/src/common/utils/withRecaptchaVerification'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { mesaColorNames, typography } from '@/src/common/theme'

export default function OnboardingComponent() {
  const logError = useLogError()
  useTrackAccountCreationPage()
  const { updateOptions, submitData } = useSardine()
  const [sendOTPToUser, { error }] = useMutation(LOGIN_USER)
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { setEmail, email } = useUserStore()
  const [emailValidationError, setEmailValidationError] = useState<string | undefined>(undefined)
  const [disableOTPButton, setDisableOTPButton] = useState(false) // flag for deactivating send otp button
  const { setAccountCreationStep } = useAccountCreationStore()

  useEffect(() => void updateOptions({ flow: 'account-creation-email' }), [updateOptions])

  const recaptchaEnabled = useFeatureGate(FeatureGates.RecaptchaEnabled).featureGate?.value

  async function sendOTP() {
    try {
      if (email) {
        void submitData()
        setDisableOTPButton(true)
        const { error } = joiEmailValidationObject.validate(email)
        if (error) {
          setEmailValidationError(error.message)
        } else {
          setEmailValidationError(undefined)
          const request = await sendOTPToUser({
            variables: { email },
            ...(recaptchaEnabled
              ? {
                  context: {
                    headers: { 'X-Recaptcha-Token': await withRecaptchaVerification('SEND_OTP') },
                  },
                }
              : undefined),
          })
          if (request.data?.requestOTPCode.requested) {
            router.replace(Routes.OnboardingVerifyOtp.route)
          }
        }
      }
    } catch (error) {
      logError(error)
    } finally {
      setDisableOTPButton(false)
    }
  }

  function submit() {
    void sendOTP()
  }

  return (
    <OnboardingWrapper paddingHorizontal={15}>
      <ScrollView style={Platform.OS === 'web' && { overflow: 'visible' }}>
        <BackButton onPress={() => setAccountCreationStep(undefined)} href={Routes.Root.route} />
        <View style={{ flex: 1 }}>
          <Text style={[typography.XXL, styles.headerTitle]}>Welcome to Mesa</Text>
          <Text style={[styles.headerCaption, typography.Base]}>
            To sign in, please enter your email
          </Text>
          <InputEmail
            placeholder="Enter your email"
            inputValue={email}
            containerStyle={{ paddingHorizontal: 0 }}
            info={{ error: emailValidationError }}
            sardineId="emailAddress"
            onChangeValue={setEmail}
            testID={AuthenticationE2E.Ids.Input.Email}
            onSubmitEditing={submit}
          />
          {/** TODO (backend):  Revisit error responses as now they are too generic */}
          {error && (
            <Text style={styles.error}>
              There was an error sending the OTP, check if your email is valid.
            </Text>
          )}
          <Text style={styles.descriptionText}>
            You will receive an email with a one time password.
          </Text>
        </View>
      </ScrollView>
      <View style={[{ marginBottom: 10 }, Platform.OS === 'web' && { marginTop: 30 }]}>
        <Button
          segmentAction="User Requested OTP"
          title="Get started"
          testID={AuthenticationE2E.Ids.Press.EmailSubmit}
          disabled={disableOTPButton}
          onPress={submit}
        />
      </View>
    </OnboardingWrapper>
  )
}

const styles = StyleSheet.create({
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 45,
  },
  headerCaption: {
    paddingTop: 20,
    paddingBottom: 32,
    color: mesaColorNames['Greyscale/600 #505D64'],
    fontSize: 16,
  },
  descriptionText: {
    color: mesaColorNames['Greyscale/400 #A4AAAC'],
    paddingVertical: 5,
    fontSize: 14,
  },
  error: {
    fontSize: 14,
    color: 'red',
    marginVertical: 10,
  },
})
